import React, { useState } from "react";
import {
  Dialog,
  Slide,
  Grid,
  CardMedia,
  Typography,
  TextField as TF,
  Button,
  Autocomplete,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { size, COLORS } from "../../utils";
import space from "../../assets/lottiefiles/space.json";
import Lottie from "react-lottie";
import { EDUCATION, WORK_EXPERIENCE } from "./Data";
import { CircularProgress } from "@mui/material";

const useStyles = makeStyles(() => ({
  cssOutlinedInput: {
    "&$cssFocused $notchedOutline": {
      borderWidth: size({ p: 0 }),
    },
  },
  cssFocused: {
    borderWidth: size({ p: 0 }),
  },
  notchedOutline: {
    borderWidth: size({ p: 0 }),
  },
}));

// ======================
//  FIXED CUSTOM FIELD
// ======================
function CustomField({ label, tf_props, params }) {
  const cls = useStyles();
  params = params || {};

  return (
    <Grid item container direction="column">
      <Grid item>
        <Typography
          sx={{
            fontFamily: "Space Mono",
            fontSize: size({ p: 0.9 }),
            fontWeight: 400,
            wordSpacing: size({ p: 0.2, neg: true }),
            marginBottom: size({ p: 0.5 }),
            color: `${COLORS.WHITE}A0`,
          }}
        >
          {label}
        </Typography>
      </Grid>

      <Grid item>
        <TF
          placeholder={label}
          {...params} // IMPORTANT — params first
          {...tf_props} // tf_props last so width stays correct
          InputProps={{
            ...params.InputProps,
            ...tf_props?.InputProps,
            classes: {
              root: cls.cssOutlinedInput,
              focused: cls.cssFocused,
              notchedOutline: cls.notchedOutline,
            },
            style: {
              fontFamily: "Space Mono",
              color: COLORS.WHITE,
              backgroundColor: "#242424",
              fontSize: size({ p: 0.9 }),
            },
          }}
          fullWidth // Keep full-width always stable
        />
      </Grid>
    </Grid>
  );
}

export default function Apply(props) {
  const [loading, setLoading] = useState(false);
  const { onClose, open, data } = props;

  const [resumeFile, setResumeFile] = useState(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    contactNumber: "",
    education: null,
    workExperience: null,
    currentWork: "",
    resumeUrl: "",
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleInputChange = (field) => (event, newValue) => {
    if (field === "education" || field === "workExperience") {
      setFormData((prev) => ({
        ...prev,
        [field]: newValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      const file = e.target.files[0];
      setResumeFile(file);

      setFormData((prev) => ({
        ...prev,
        resumeUrl: "Attached Resume",
      }));
    }
  };

  const sendEmail = async () => {
    if (!formData.email) {
      alert("Please fill the email field");
      return;
    }

    setLoading(true);

    try {
      let base64File = null;
      let fileName = null;

      if (resumeFile) {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result;
            resolve(result.split(",")[1]);
          };
          reader.onerror = reject;
          reader.readAsDataURL(resumeFile);
        });

        base64File = base64;
        fileName = resumeFile.name;
      }

      const bodyText = `
First Name: ${formData.firstName}
Last Name: ${formData.lastName}
Email: ${formData.email}
Contact Number: ${formData.contactNumber}
Education: ${formData.education ? formData.education.label : ""}
Work Experience: ${formData.workExperience ? formData.workExperience.label : ""}
Current Work: ${formData.currentWork}
Resume: ${resumeFile ? "Attached" : "No File"}
Position Applied: ${data.Title}
    `;

      const payload = {
        subject: `New Job Application - ${data.Title}`,
        body: bodyText,
        base64_data: base64File,
        filename: fileName,
      };

      const res = await fetch("http://api.thinkgrail.com/genesysemailapi/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed to send email");

      alert("Application sent successfully!");
      onClose();
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        position: "",
        message: "",
      });

      setResumeFile(null);
      setLoading(false);
    } catch (error) {
      console.error(error);
      alert("Failed to send application, please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} TransitionComponent={Slide} fullScreen>
      <div
        style={{
          width: "100%",
          height: "100%",
          backgroundColor: COLORS.WHITE,
          backgroundImage: `
            linear-gradient(${COLORS.C5}40 ${size({
            p: 0.06,
          })}, transparent ${size({
            p: 0.06,
          })}),
            linear-gradient(to right, ${COLORS.C5}40 ${size({
            p: 0.06,
          })}, ${COLORS.WHITE} ${size({ p: 0.06 })})
          `,
          backgroundSize: `${size({ p: 2.5 })} ${size({ p: 2 })}`,
          position: "relative",
          display: isMobile ? "flex" : "grid",
          flexDirection: isMobile ? "column" : undefined,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            position: "absolute",
            left: isMobile ? "20%" : size({ p: 60 }),
            top: isMobile ? "2%" : undefined,
          }}
        >
          <Lottie
            width={isMobile ? size({ p: 55 }) : size({ p: 40 })}
            height={isMobile ? size({ p: 55 }) : size({ p: 40 })}
            options={{
              loop: true,
              autoplay: true,
              animationData: space,
            }}
          />
        </div>

        <div
          style={{
            marginTop: isMobile ? size({ p: 60 }) : 0,
            marginLeft: isMobile ? 0 : size({ p: -40 }),
            position: "relative",
            width: isMobile ? "90%" : undefined,
          }}
        >
          <Grid
            container
            direction="column"
            alignItems="center"
            justifyContent="start"
            rowGap={size({ p: 1.5 })}
            sx={{
              backgroundColor: COLORS.C5,
              borderRadius: size({ p: 0.6 }),
              padding: isMobile ? size({ p: 3 }) : `0px ${size({ p: 2 })}`,
              width: isMobile ? "100%" : size({ p: 50 }),
              height: isMobile ? "auto" : size({ p: 93, h: true }),
            }}
          >
            {/* Close */}
            <Grid item>
              <Typography
                onClick={onClose}
                sx={{
                  fontFamily: "Space Mono",
                  fontSize: isMobile ? size({ p: 5 }) : size({ p: 3 }),
                  color: COLORS.WHITE,
                  position: "absolute",
                  cursor: "pointer",
                  right: size({ p: 2 }),
                  top: size({ p: 2 }),
                }}
              >
                x
              </Typography>
            </Grid>

            {/* Header */}
            <Grid
              container
              alignItems="center"
              justifyContent="start"
              columnGap={size({ p: 1 })}
            >
              <Grid item>
                <CardMedia
                  component="img"
                  image="/assets/images/careers/apply.png"
                  sx={{
                    width: isMobile ? size({ p: 8 }) : size({ p: 5 }),
                    height: isMobile ? size({ p: 8 }) : size({ p: 5 }),
                  }}
                />
              </Grid>
              <Grid item>
                <Typography
                  sx={{
                    fontFamily: "Space Mono",
                    fontSize: isMobile ? size({ p: 2 }) : size({ p: 1 }),
                    fontWeight: 400,
                    color: `${COLORS.WHITE}A0`,
                    textAlign: "left",
                  }}
                >
                  Apply To Position
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Space Mono",
                    fontSize: isMobile ? size({ p: 3.4 }) : size({ p: 1.4 }),
                    fontWeight: 600,
                    color: COLORS.WHITE,
                    textAlign: "left",
                  }}
                >
                  {data.Title}
                </Typography>
              </Grid>
            </Grid>

            {/* Form Section */}
            <Grid
              container
              direction={isMobile ? "column" : "row"}
              columnGap={size({ p: 1 })}
              rowGap={size({ p: 1 })}
            >
              {/* Left */}
              <Grid
                item
                container
                direction="column"
                rowGap={size({ p: 1 })}
                xs
              >
                <CustomField
                  label="First Name"
                  tf_props={{
                    fullWidth: true,
                    value: formData.firstName,
                    onChange: handleInputChange("firstName"),
                  }}
                />

                <CustomField
                  label="Email Address"
                  tf_props={{
                    fullWidth: true,
                    value: formData.email,
                    onChange: handleInputChange("email"),
                    type: "email",
                  }}
                />

                <Autocomplete
                  options={EDUCATION}
                  getOptionLabel={(option) => option.label || ""}
                  value={formData.education}
                  onChange={handleInputChange("education")}
                  renderInput={(params) => (
                    <CustomField params={params} label="Education" />
                  )}
                />

                <CustomField
                  label="Describe Your Current Work"
                  tf_props={{
                    fullWidth: true,
                    multiline: true,
                    rows: isMobile ? 1 : 5,
                    value: formData.currentWork,
                    onChange: handleInputChange("currentWork"),
                  }}
                />
              </Grid>

              {/* Right */}
              <Grid
                item
                container
                direction="column"
                rowGap={size({ p: 1 })}
                xs
              >
                <CustomField
                  label="Last Name"
                  tf_props={{
                    fullWidth: true,
                    value: formData.lastName,
                    onChange: handleInputChange("lastName"),
                  }}
                />

                <CustomField
                  label="Contact Number"
                  tf_props={{
                    fullWidth: true,
                    value: formData.contactNumber,
                    onChange: handleInputChange("contactNumber"),
                    type: "tel",
                  }}
                />

                <Autocomplete
                  options={WORK_EXPERIENCE}
                  getOptionLabel={(option) => option.label || ""}
                  value={formData.workExperience}
                  onChange={handleInputChange("workExperience")}
                  renderInput={(params) => (
                    <CustomField params={params} label="Work Experience" />
                  )}
                />

                {/* Resume Upload */}
                <Grid
                  item
                  container
                  direction="column"
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    border: `${size({ p: 0.4 })} dashed ${COLORS.WHITE}C0`,
                    borderRadius: size({ p: 1 }),
                    padding: size({ p: 2 }),
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Space Mono",
                      fontSize: isMobile ? size({ p: 2 }) : size({ p: 1 }),
                      color: `${COLORS.WHITE}A0`,
                    }}
                  >
                    Attach Your Resume (only filename shown)
                  </Typography>

                  <Button
                    variant="contained"
                    component="label"
                    sx={{
                      backgroundColor: COLORS.C6,
                      fontFamily: "Space Mono",
                      fontSize: isMobile ? size({ p: 1.8 }) : size({ p: 0.8 }),
                      color: COLORS.WHITE,
                      textTransform: "none",
                    }}
                  >
                    Upload
                    <input
                      type="file"
                      hidden
                      accept=".pdf"
                      onChange={handleFileChange}
                    />
                  </Button>

                  <Typography
                    sx={{
                      fontFamily: "Space Mono",
                      fontSize: isMobile ? size({ p: 1.9 }) : size({ p: 0.9 }),
                      color: COLORS.WHITE,
                      border: `${size({ p: 0.1 })} solid ${COLORS.WHITE}C0`,
                      padding: size({ p: 0.5 }),
                      borderRadius: size({ p: 0.4 }),
                      marginTop: size({ p: 1 }),
                      textAlign: "center",
                      maxWidth: "100%",
                    }}
                  >
                    {resumeFile
                      ? resumeFile.name.length > 20
                        ? resumeFile.name.slice(0, 10) +
                          "..." +
                          resumeFile.name.slice(-10)
                        : resumeFile.name
                      : "No File Chosen"}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            {/* Submit */}
            <Grid
              container
              direction="column"
              rowGap={size({ p: 0.5 })}
              sx={{ marginTop: size({ p: 1 }) }}
            >
              <Button
                variant="contained"
                fullWidth
                onClick={loading ? null : sendEmail}
                sx={{
                  backgroundColor: COLORS.C6,
                  fontFamily: "Space Mono",
                  fontWeight: 400,
                  fontSize: isMobile ? size({ p: 2.2 }) : size({ p: 1 }),
                  color: COLORS.WHITE,
                  textTransform: "none",
                }}
              >
                {loading && (
                  <CircularProgress
                    size={size({ p: 1.3 })}
                    sx={{ color: COLORS.WHITE, mr:'1rem' }}
                  />
                )}
                Submit
              </Button>
            </Grid>
          </Grid>
        </div>
      </div>
    </Dialog>
  );
}
